import { getIdentityEnvironment } from '@app/common';
import { getCurrentHref } from './locationUtils';

const SSO_REDIRECT_PATH =
  '/forums/s/plugins/common/feature/oauth2sso_v2/sso_login_redirect';

const COMMUNITY_PARAM = 'application=community--entry-auth';

/**
 * Returns the environment-appropriate Khoros endpoint path for logout.
 * community-library uses 'atlassiandev' for staging and 'atlassian' for prod.
 */
export const getKhorosLogoutPath = (): string => {
  const env = getIdentityEnvironment();
  const khorosEndpoint = env === 'production' ? 'atlassian' : 'atlassiandev';
  return `/forums/s/plugins/custom/atlassian/${khorosEndpoint}/khoros_logout`;
};

/**
 * Builds the forums SSO sign-in URL used as the `continue` param in the identity login URL.
 * Mirrors the logic of community-library's getForumsSignInUrl, which:
 *   1. encodes the raw page URL once for the `referer` param
 *   2. encodes the full forums redirect URL once for use in `continue=`
 * Reads window.location.href directly so it always reflects the current page at call time.
 */
export const getForumsSignInUrl = (): string => {
  const homeBaseUrl = process.env.HOME_BASE_URL;
  const referer = encodeURIComponent(getCurrentHref());
  const forumsUrl = `${homeBaseUrl}${SSO_REDIRECT_PATH}?referer=${referer}`;
  return encodeURIComponent(forumsUrl);
};

/**
 * Builds the forums sign-out URL used as the `continue` param in the identity logout URL.
 * Mirrors the logic of community-library's getForumsSignOutUrl.
 * Reads window.location.href directly so it always reflects the current page at call time.
 */
export const getForumsSignOutUrl = (): string => {
  const homeBaseUrl = process.env.HOME_BASE_URL;
  const referer = encodeURIComponent(getCurrentHref());
  const forumsUrl = `${homeBaseUrl}${getKhorosLogoutPath()}?referer=${referer}`;
  return encodeURIComponent(forumsUrl);
};

export const getLoginHref = (email: string): string => {
  const identityBaseUrl = process.env.REACT_APP_IDENTITY_BASE_URL;
  const params = [COMMUNITY_PARAM];
  if (email) params.push(`login_hint=${email}`);
  // getForumsSignInUrl already returns an encoded URL — use it directly as continue= value
  params.push(`continue=${getForumsSignInUrl()}`);
  return `${identityBaseUrl}/login?${params.join('&')}`;
};

export const getLogoutHref = (email: string): string => {
  const identityBaseUrl = process.env.REACT_APP_IDENTITY_BASE_URL;
  const params = [COMMUNITY_PARAM];
  if (email) params.push(`login_hint=${email}`);
  // getForumsSignOutUrl already returns an encoded URL — use it directly as continue= value
  params.push(`continue=${getForumsSignOutUrl()}`);
  return `${identityBaseUrl}/logout?${params.join('&')}`;
};

export const getSignupHref = (email: string): string => {
  const identityBaseUrl = process.env.REACT_APP_IDENTITY_BASE_URL;
  const params = [COMMUNITY_PARAM];
  if (email) params.push(`login_hint=${email}`);
  // Signup goes directly back to the page, so encode the raw URL once
  params.push(`continue=${encodeURIComponent(getCurrentHref())}`);
  return `${identityBaseUrl}/signup?${params.join('&')}`;
};

export const getSwitchHref = (): string => {
  const identityBaseUrl = process.env.REACT_APP_IDENTITY_BASE_URL;
  const params = [COMMUNITY_PARAM];
  params.push(`continue=${getForumsSignInUrl()}`);
  params.push(`prompt=select_account`);
  return `${identityBaseUrl}/login?${params.join('&')}`;
};
